import { memo, useEffect, useMemo, useState } from "react";
import { Crown, User } from "lucide-react";
import { computeLiveBreakdown } from "../lib/billing";

function pad(n) {
  return String(n).padStart(2, "0");
}

function formatDuration(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return { h: pad(h), m: pad(m), s: pad(sec) };
}

function computeLiveAmount(sess, nowMs, settings) {
  if (!sess || !settings) return 0;
  const rateNormal =
    sess.player_type === "member"
      ? sess.rate_member ?? settings.rate_member
      : sess.player_type === "cross_club_member"
      ? sess.rate_cross_club_member ?? settings.rate_cross_club_member ?? sess.rate_member ?? settings.rate_member
      : sess.rate_non_member ?? settings.rate_non_member;
  return computeLiveBreakdown(sess, nowMs, settings, rateNormal).amount;
}

function TableCard({
  table,
  settings,
  onStart,
  onStop,
}) {
  const isRunning = table.status === "running" && table.current_session;
  const sess = table.current_session;

  // Tick our own clock once a second, but ONLY while this table is running —
  // an idle table has nothing time-based to show, so it should never
  // re-render on a timer. This keeps a full grid of tables from all
  // re-rendering every second just because a handful are actually running.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isRunning) return undefined;
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [isRunning]);

  const liveMs = sess ? now - new Date(sess.start_time).getTime() : 0;
  const { h, m, s } = formatDuration(liveMs);
  const liveAmount = useMemo(
    () => (isRunning ? computeLiveAmount(sess, now, settings) : 0),
    [isRunning, sess, now, settings]
  );

  const currency = settings?.currency_symbol || "₹";

  return (
    <div
      data-testid={`table-card-${table.id}`}
      className={`relative aspect-[3/4] min-h-[360px] sm:min-h-[400px] rounded-[26px] transition-transform duration-300 hover:-translate-y-1 snooker-wood ${
        isRunning ? "snooker-wood--running" : ""
      }`}
    >
      {/* Pockets — rendered on the BROWN WOOD rail, not inside the felt */}
      <span className="snooker-pocket snooker-pocket--tl" />
      <span className="snooker-pocket snooker-pocket--tr" />
      <span className="snooker-pocket snooker-pocket--bl" />
      <span className="snooker-pocket snooker-pocket--br" />
      <span className="snooker-pocket snooker-pocket--ml" />
      <span className="snooker-pocket snooker-pocket--mr" />

      {/* Felt surface */}
      <div className="absolute inset-[18px] rounded-[16px] snooker-felt overflow-hidden flex flex-col">
        {/* Top: name + status */}
        <div className="relative z-10 pt-10 sm:pt-12 px-4 text-center">
          <div
            data-testid={`table-name-${table.id}`}
            className="text-3xl sm:text-4xl font-black uppercase tracking-wide text-black drop-shadow-sm leading-none"
          >
            {table.name}
          </div>
          <div
            data-testid={`status-${table.id}`}
            className="mt-2 text-xs sm:text-sm uppercase font-extrabold tracking-[0.25em] text-black/85"
          >
            {isRunning ? "Running" : "Available"}
          </div>
        </div>

        {/* Middle: live info when running, else nothing (rate label removed) */}
        <div className="relative z-10 flex-1 px-4 mt-3 flex flex-col items-center justify-center">
          {isRunning ? (
            <div className="text-center space-y-2">
              <div className="flex items-center justify-center gap-1.5 text-black/90">
                {sess.player_type === "member" || sess.player_type === "cross_club_member" ? (
                  <Crown className="w-4 h-4" />
                ) : (
                  <User className="w-4 h-4" />
                )}
                <span
                  data-testid={`player-name-${table.id}`}
                  className={`text-base font-bold truncate max-w-[180px] ${
                    sess.player_name === "Player" ? "italic text-black/60" : ""
                  }`}
                >
                  {sess.player_name === "Player" ? "Tap Stop to name" : sess.player_name}
                </span>
              </div>
              <div
                data-testid={`timer-${table.id}`}
                className="font-mono font-black text-3xl sm:text-4xl text-black tabular-nums leading-none"
              >
                {h}
                <span className="timer-colon">:</span>
                {m}
                <span className="timer-colon">:</span>
                {s}
              </div>
              <div
                data-testid={`amount-${table.id}`}
                className="font-mono font-black text-xl text-black/90 tabular-nums"
              >
                {currency}
                {liveAmount.toFixed(2)}
              </div>
            </div>
          ) : null}
        </div>

        {/* Bottom: red pill button */}
        <div className="relative z-10 px-6 pb-7 sm:pb-8">
          {isRunning ? (
            <button
              type="button"
              onClick={() => onStop(table)}
              data-testid={`stop-session-btn-${table.id}`}
              className="snooker-pill snooker-pill--stop w-full"
            >
              Stop Session
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onStart(table)}
              data-testid={`start-session-btn-${table.id}`}
              className="snooker-pill snooker-pill--start w-full"
            >
              Start Session
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(TableCard);
